import {
    encodeUrlSearchParams,
    fetchAll,
    fetchWithToken,
    ApiResponse,
    PaginatedApiResponseData,
    ContentTag,
} from '@round/api';
import { InstagramInfluencerPost } from '../Advertising/InfluencerPlan/types/Instagram.types';
import React, { Dispatch, useCallback, createContext, useReducer, ReactNode, useState } from 'react';
import { ReducerAction, ReducerActionWithPayload } from '../../App.types';
import { createReducer } from '../../helpers';

type GetInfluencerContentTagsParams = Partial<{
    search: string;
    page?: number;
    page_size?: number;
}>;
type State = {
    contentTags: ContentTag[];
    isLoading: boolean;
    isInitialized: boolean;
    error: string | null;
};

type Actions =
    | ReducerActionWithPayload<'initialized', ContentTag[]>
    | ReducerAction<'load'>
    | ReducerActionWithPayload<'error', string | null>;

const reducer = createReducer<State, Actions>({
    initialized: (state, { payload }) => ({
        ...state,
        contentTags: payload,
        error: null,
        isInitialized: true,
        isLoading: false,
    }),
    error: (state, { payload }) => ({
        ...state,
        error: payload,
        isInitialized: true,
        isLoading: false,
    }),
    load: (state) => ({
        ...state,
        isLoading: true,
    }),
});

const ContentTagsContext = createContext<State | null>(null);
const ContentTagsDispatchContext = createContext<Dispatch<Actions> | null>(null);

export const ContentTagsProvider = ({ children }: { children?: ReactNode }) => {
    const [state, dispatch] = useReducer(reducer, {
        contentTags: [],
        error: null,
        isInitialized: false,
        isLoading: false,
    });

    return React.createElement(
        ContentTagsContext.Provider,
        { value: state },
        React.createElement(ContentTagsDispatchContext.Provider, { value: dispatch }, children)
    );
};

type GetInstagramInfluencerPostsParams = {
    influencerId: number;
};
export const getAllInstagramInfluencerPosts = async (
    params: GetInstagramInfluencerPostsParams,
    requestInit?: RequestInit
): Promise<InstagramInfluencerPost[]> => {
    const queryParams = encodeUrlSearchParams({
        influencer_id: params.influencerId,
    });

    return await fetchAll(`/api/advertising/viewsets/instagram-influencer-post/${queryParams}`, requestInit);
};

export async function getInfluencerContentTags(
    params: GetInfluencerContentTagsParams,
    requestInit?: RequestInit
): Promise<ApiResponse<PaginatedApiResponseData<ContentTag>, 200>> {
    const queryParams = encodeUrlSearchParams(params);
    const response = await fetchWithToken(`/api/influencer/content-tags/${queryParams}`, requestInit);

    if (!response.ok) {
        throw new Error('Could not fetch content tags');
    }

    return {
        status: 200,
        data: await response.json(),
    };
}

export function useInfluencerContentTags(params?: GetInfluencerContentTagsParams) {
    const [tags, setTags] = useState<ContentTag[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isInitialized, setIsInitialized] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const init = useCallback(
        async (requestInit?: RequestInit) => {
            try {
                setIsLoading(true);
                setError(null);
                const response = await getInfluencerContentTags(
                    {
                        page: 1,
                        page_size: 1000,
                        search: '',
                        ...params, // Override with passed params
                    },
                    requestInit
                );
                setTags(response.data.results);
                setIsInitialized(true);
            } catch (e) {
                if (e instanceof Error && e.name === 'AbortError') {
                    return;
                }
                setError('Could not fetch content tags');
            } finally {
                setIsLoading(false);
            }
        },
        [params]
    );

    return {
        tags,
        isLoading,
        isInitialized,
        error,
        init,
    };
}
